/**
 * "Your NBA": which single game leads the personal home. One card, one action (Catch me up).
 * Current games lead. Old participation stays in the separate history, never in discovery.
 */
import type { FeedCard } from './feed';
import type { HistoryParticipation } from '../director/api';
import type { LiveGameContext } from '@clutch/core';

export type LatestGame =
  | { kind: 'card'; reason: 'live' | 'final' | 'upcoming' | 'classic' | 'featured'; card: FeedCard; mine: boolean }
  | { kind: 'room'; reason: 'history'; roomId: string; away: string; home: string; result?: 'correct' | 'incorrect' | 'void'; finalScore?: string; mine: boolean };

const involves = (c: Pick<FeedCard, 'away' | 'home'>, teams: readonly string[]) => teams.includes(c.away.tricode) || teams.includes(c.home.tricode);
const words = (name: string) => name.normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim().split(/\s+/).filter(Boolean);
/** Whole name tokens only: 'Hall' must not match 'Haliburton'. Supports reported surnames. */
export function playerMatches(preference: string, reported: string): boolean {
  const wanted = words(preference), actual = words(reported);
  return !!wanted.length && !!actual.length && (wanted.every((p) => actual.includes(p)) || actual.every((p) => wanted.includes(p)));
}

export function latestGame(o: {
  followed: readonly string[];
  players?: readonly string[];
  contexts?: readonly LiveGameContext[];
  live: readonly FeedCard[];
  classics: readonly FeedCard[];
  participation: readonly HistoryParticipation[];
  now?: number;
  /** Fixture-driver demos only. Public discovery never falls back to the archive. */
  allowArchive?: boolean;
}): LatestGame | undefined {
  const { followed } = o;
  const mine = (c: FeedCard) => involves(c, followed) || (o.players ?? []).some((p) => [
    ...c.stars.map((s) => s.name),
    ...Object.values(o.contexts?.find((x) => x.gameId === c.gameId)?.state.players ?? {}).map((s) => s.name),
  ].some((name) => playerMatches(p, name)));
  const current = currentWeek(o.live, o.now);
  const live = current.filter((c) => c.kind === 'live').sort((a, b) => Number(mine(b)) - Number(mine(a)))[0];
  if (live) return { kind: 'card', reason: 'live', card: live, mine: mine(live) };
  const final = current.filter((c) => c.kind === 'final').sort((a, b) => Number(mine(b)) - Number(mine(a)) || (b.startUtc ?? '').localeCompare(a.startUtc ?? ''))[0];
  if (final) return { kind: 'card', reason: 'final', card: final, mine: mine(final) };
  const upcoming = current.filter((c) => c.kind === 'tonight').sort((a, b) => Number(mine(b)) - Number(mine(a)) || (a.startUtc ?? '').localeCompare(b.startUtc ?? ''))[0];
  if (upcoming) return { kind: 'card', reason: 'upcoming', card: upcoming, mine: mine(upcoming) };
  if (!o.allowArchive) return undefined;
  const classicMine = o.classics.find(mine);
  if (classicMine) return { kind: 'card', reason: 'classic', card: classicMine, mine: true };
  const hero = o.classics[0];
  return hero ? { kind: 'card', reason: 'featured', card: hero, mine: false } : undefined;
}

/** Seven Eastern calendar days, not “any game ever returned by a provider”. */
export function currentWeek(cards: readonly FeedCard[], now = Date.now()): FeedCard[] {
  const date = (time: number) => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' }).format(time);
  const today = date(now), from = new Date(`${today}T12:00:00Z`);
  from.setUTCDate(from.getUTCDate() - 6);
  const first = from.toISOString().slice(0, 10);
  return cards.filter((c) => {
    const time = Date.parse(c.startUtc ?? '');
    if (!Number.isFinite(time) || c.kind === 'classic') return false;
    const day = date(time);
    return day >= first && day <= today && (c.kind !== 'tonight' || day === today) && (c.kind !== 'live' || now - time < 36 * 3600_000);
  });
}
